import React from 'react';
import { useLanguage } from '../lib/LanguageContext';
import TermsAndConditionsFr from './fr/TermsAndConditionsFr';
import '../styles/Document.css';

function TermsAndConditions() {
  const { isFr } = useLanguage();
  if (isFr) return <TermsAndConditionsFr />;

  return (
    <div className="doc-page">
      <div className="doc-header">
        <span className="doc-label">General</span>
        <h1 className="doc-title">MegaTransact Terms and Conditions</h1>
        <p className="doc-intro">
          The rules, responsibilities, and conditions that apply when you use MegaTransact.
          MegaTransact is a digital platform beginning with salary-based lending and expanding
          progressively into practical money services. By using MegaTransact, you agree to use
          the platform responsibly and in accordance with these Terms and Conditions.
        </p>
        <div className="doc-meta">
          <span className="doc-meta-item">
            <strong>Effective Date:</strong> March 27, 2026
          </span>
          <span className="doc-meta-item">
            <strong>Last Updated:</strong> March 27, 2026
          </span>
        </div>
      </div>

      <div className="doc-body">
        <div className="doc-preamble">
          <p>
            These Terms and Conditions govern your access to and use of MegaTransact, including
            our app, website, support channels, and related services.
          </p>
          <p>
            By creating an account, accessing MegaTransact, or using any part of the platform,
            you agree to these Terms and Conditions. If you do not agree, you should not use
            MegaTransact.
          </p>
        </div>

        {/* Section 1 */}
        <div className="doc-section">
          <span className="section-number">Section 1</span>
          <h2 className="section-title">Who We Are</h2>
          <p>
            MegaTransact is a digital platform designed to provide structured salary-linked
            financial support and related digital services.
          </p>
          <p>
            For questions about these Terms and Conditions, you may contact us at:
          </p>
          <div className="doc-contact-box">
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
            <p>
              <strong>Company:</strong> MegaBliss LTD
            </p>
            <p>
              <strong>Address:</strong> Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45
            </p>
          </div>
        </div>

        {/* Section 2 */}
        <div className="doc-section">
          <span className="section-number">Section 2</span>
          <h2 className="section-title">Eligibility</h2>
          <p>
            To use MegaTransact, you must meet any eligibility requirements that apply to the
            relevant service.
          </p>
          <p>Depending on the service, this may include:</p>
          <ul>
            <li>Being of legal age to enter into a binding agreement</li>
            <li>Providing accurate and complete information</li>
            <li>
              Being employed by an approved employer or workplace partner, where required
            </li>
            <li>
              Meeting any salary-based or service-specific eligibility criteria
            </li>
            <li>Using MegaTransact only for lawful and legitimate purposes</li>
          </ul>
          <p>
            MegaTransact may refuse access, suspend onboarding, or limit service availability
            where eligibility requirements are not met.
          </p>
        </div>

        {/* Section 3 */}
        <div className="doc-section">
          <span className="section-number">Section 3</span>
          <h2 className="section-title">Account Registration</h2>
          <p>
            To use certain MegaTransact services, you may need to create an account.
          </p>
          <p>When creating and using an account, you agree to:</p>
          <ul>
            <li>Provide true, accurate, and current information</li>
            <li>Keep your login details secure</li>
            <li>Maintain and update your information when necessary</li>
            <li>
              Accept responsibility for activity conducted through your account
            </li>
            <li>
              Notify MegaTransact promptly if you suspect unauthorized access or misuse
            </li>
          </ul>
          <p>
            MegaTransact is not responsible for losses arising from your failure to safeguard
            your account credentials.
          </p>
        </div>

        {/* Section 4 */}
        <div className="doc-section">
          <span className="section-number">Section 4</span>
          <h2 className="section-title">Description of Services</h2>
          <p>
            MegaTransact is starting with salary-based lending and may expand into additional
            practical money services over time.
          </p>
          <p>Services may include:</p>
          <ul>
            <li>Access to salary-linked financial support</li>
            <li>Application and approval workflows</li>
            <li>Employer-linked repayment arrangements</li>
            <li>Payout, repayment, or collection support</li>
            <li>Account management tools</li>
            <li>Communication and support tools</li>
            <li>Other related services introduced from time to time</li>
          </ul>
          <p>Not all services will be available to all users at all times.</p>
          <p>
            MegaTransact may add, modify, suspend, or discontinue services without guaranteeing
            continuous availability of any specific feature.
          </p>
        </div>

        {/* Section 5 */}
        <div className="doc-section">
          <span className="section-number">Section 5</span>
          <h2 className="section-title">Salary-Based Lending Terms</h2>
          <p>
            Where MegaTransact provides access to salary-based lending, additional
            service-specific terms may apply alongside these Terms and Conditions.
          </p>
          <p>By requesting or accepting salary-based support through MegaTransact, you agree that:</p>
          <ul>
            <li>Any application is subject to review and approval</li>
            <li>Approval is not guaranteed</li>
            <li>
              Any approved amount, duration, repayment structure, and related conditions will
              be communicated to you before completion
            </li>
            <li>
              Repayment may be linked to salary or employer-supported arrangements where
              applicable
            </li>
            <li>
              You remain responsible for meeting agreed repayment obligations
            </li>
            <li>
              MegaTransact may decline, limit, suspend, or cancel access where risk,
              eligibility, compliance, or operational concerns arise
            </li>
          </ul>
          <p>Nothing in these Terms requires MegaTransact to approve any request.</p>
        </div>

        {/* Section 6 */}
        <div className="doc-section">
          <span className="section-number">Section 6</span>
          <h2 className="section-title">Accurate Information and Verification</h2>
          <p>
            You agree to provide accurate, complete, and non-misleading information at all times.
          </p>
          <p>
            MegaTransact may request documents, confirmations, or additional details in order to:
          </p>
          <ul>
            <li>Verify your identity</li>
            <li>Confirm your employment or workplace relationship</li>
            <li>Assess eligibility</li>
            <li>Prevent fraud or misuse</li>
            <li>Comply with operational or legal requirements</li>
          </ul>
          <p>
            If you provide false, incomplete, outdated, or misleading information, MegaTransact
            may suspend or terminate your access and may take further action where appropriate.
          </p>
        </div>

        {/* Section 7 */}
        <div className="doc-section">
          <span className="section-number">Section 7</span>
          <h2 className="section-title">Employer and Workplace Arrangements</h2>
          <p>Some MegaTransact services depend on employer or workplace participation.</p>
          <p>Where this applies, you acknowledge that:</p>
          <ul>
            <li>
              MegaTransact may rely on information received from employers or workplace partners
            </li>
            <li>
              Employer-linked processes may affect eligibility, approval, disbursement, and
              repayment arrangements
            </li>
            <li>
              Service availability may depend on continued cooperation between MegaTransact and
              the relevant employer or workplace partner
            </li>
          </ul>
          <p>
            MegaTransact is not responsible for delays, interruptions, or limitations caused by
            employer-side decisions, payroll timing, or workplace administrative processes.
          </p>
        </div>

        {/* Section 8 */}
        <div className="doc-section">
          <span className="section-number">Section 8</span>
          <h2 className="section-title">Fees, Charges, and Repayment Obligations</h2>
          <p>
            Where applicable, MegaTransact will communicate relevant amounts, charges, repayment
            schedules, and other key terms before a user completes or accepts a service.
          </p>
          <p>You agree that:</p>
          <ul>
            <li>
              You will review the applicable terms carefully before accepting a service
            </li>
            <li>
              You are responsible for repaying amounts that you validly accept
            </li>
            <li>
              Failure to meet repayment obligations may result in reminders, restrictions,
              collections activity, suspension of access, or other lawful recovery steps
            </li>
            <li>
              Outstanding obligations may affect your future eligibility for MegaTransact
              services
            </li>
          </ul>
          <p>
            MegaTransact may correct obvious errors in displayed amounts, calculations, or
            processing where reasonably necessary.
          </p>
        </div>

        {/* Section 9 */}
        <div className="doc-section">
          <span className="section-number">Section 9</span>
          <h2 className="section-title">User Responsibilities</h2>
          <p>When using MegaTransact, you agree to:</p>
          <ul>
            <li>Use the platform lawfully and responsibly</li>
            <li>
              Follow all instructions and processes reasonably provided by MegaTransact
            </li>
            <li>Keep your information up to date</li>
            <li>
              Cooperate with reasonable verification and security checks
            </li>
            <li>
              Respect the rights of MegaTransact, its staff, partners, and other users
            </li>
            <li>
              Use the platform only for your own legitimate purposes unless expressly authorized
              otherwise
            </li>
          </ul>
        </div>

        {/* Section 10 */}
        <div className="doc-section">
          <span className="section-number">Section 10</span>
          <h2 className="section-title">Prohibited Conduct</h2>
          <p>You must not:</p>
          <ul>
            <li>
              Use MegaTransact for unlawful, fraudulent, deceptive, or abusive purposes
            </li>
            <li>
              Open or operate accounts using false identities or unauthorized information
            </li>
            <li>
              Attempt to bypass eligibility, verification, or security controls
            </li>
            <li>
              Interfere with the platform's operation, systems, or security
            </li>
            <li>
              Upload malicious code, harmful content, or misleading materials
            </li>
            <li>
              Misrepresent your employment, salary, repayment ability, or identity
            </li>
            <li>
              Use MegaTransact in a way that harms other users, employers, or partners
            </li>
            <li>
              Copy, exploit, reverse-engineer, or misuse the platform except as permitted by
              law
            </li>
          </ul>
          <p>
            MegaTransact may investigate suspected misconduct and take appropriate action,
            including suspension, termination, reporting, or recovery steps where justified.
          </p>
        </div>

        {/* Section 11 */}
        <div className="doc-section">
          <span className="section-number">Section 11</span>
          <h2 className="section-title">Privacy</h2>
          <p>
            Your use of MegaTransact is also subject to the MegaTransact Privacy Policy, which
            explains how personal information is collected, used, stored, shared, and protected.
          </p>
          <p>
            By using MegaTransact, you acknowledge that your information may be handled in
            accordance with that policy.
          </p>
        </div>

        {/* Section 12 */}
        <div className="doc-section">
          <span className="section-number">Section 12</span>
          <h2 className="section-title">Third-Party Providers and Services</h2>
          <p>
            MegaTransact may rely on third-party providers for hosting, verification, analytics,
            communications, payouts, collections support, or related operational functions.
          </p>
          <p>
            MegaTransact may also integrate with employer systems, payroll processes, payment
            channels, or technical service providers where relevant.
          </p>
          <p>
            We are not responsible for independent third-party services outside our reasonable
            control, including failures caused by third-party networks, devices, service outages,
            or external processing systems.
          </p>
        </div>

        {/* Section 13 */}
        <div className="doc-section">
          <span className="section-number">Section 13</span>
          <h2 className="section-title">Service Availability</h2>
          <p>
            MegaTransact aims to provide a reliable platform, but we do not guarantee
            uninterrupted or error-free service at all times.
          </p>
          <p>Access may be affected by:</p>
          <ul>
            <li>Maintenance</li>
            <li>Technical issues</li>
            <li>Security checks</li>
            <li>Third-party outages</li>
            <li>Regulatory or operational changes</li>
            <li>Device, network, or connectivity problems</li>
          </ul>
          <p>
            MegaTransact may suspend, restrict, or withdraw access temporarily or permanently
            where reasonably necessary.
          </p>
        </div>

        {/* Section 14 */}
        <div className="doc-section">
          <span className="section-number">Section 14</span>
          <h2 className="section-title">Intellectual Property</h2>
          <p>
            All content, branding, software, workflows, designs, text, graphics, logos, and
            platform materials made available through MegaTransact are owned by MegaTransact or
            its licensors, unless otherwise stated.
          </p>
          <p>
            You may use the platform only for its intended purpose. You may not copy, reproduce,
            distribute, modify, sell, or exploit MegaTransact materials without prior written
            permission, except where permitted by law.
          </p>
        </div>

        {/* Section 15 */}
        <div className="doc-section">
          <span className="section-number">Section 15</span>
          <h2 className="section-title">Suspension and Termination</h2>
          <p>
            MegaTransact may suspend, restrict, or terminate your access to the platform or to
            specific services where reasonably necessary, including where:
          </p>
          <ul>
            <li>You breach these Terms and Conditions</li>
            <li>You provide false or misleading information</li>
            <li>You fail to meet eligibility requirements</li>
            <li>There is suspected fraud, misuse, or security risk</li>
            <li>There are outstanding obligations or unresolved disputes</li>
            <li>
              Continued access is not operationally, legally, or commercially appropriate
            </li>
          </ul>
          <p>
            You may also stop using MegaTransact at any time. However, ending use of the platform
            does not remove any obligations that already arose before termination, including
            repayment obligations.
          </p>
        </div>

        {/* Section 16 */}
        <div className="doc-section">
          <span className="section-number">Section 16</span>
          <h2 className="section-title">Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by applicable law, MegaTransact will not be liable
            for indirect, incidental, special, consequential, or punitive losses arising from
            your use of, or inability to use, the platform.
          </p>
          <p>This includes, where permitted by law, losses relating to:</p>
          <ul>
            <li>Delays</li>
            <li>Service interruptions</li>
            <li>Third-party failures</li>
            <li>Lost opportunities</li>
            <li>Loss of profits</li>
            <li>Loss of data</li>
            <li>Device or connectivity issues</li>
          </ul>
          <p>
            Nothing in these Terms excludes liability that cannot lawfully be excluded.
          </p>
        </div>

        {/* Section 17 */}
        <div className="doc-section">
          <span className="section-number">Section 17</span>
          <h2 className="section-title">Indemnity</h2>
          <p>
            You agree to be responsible for losses, claims, costs, or damages arising from your
            misuse of MegaTransact, your breach of these Terms and Conditions, or your violation
            of applicable law or the rights of others, to the extent permitted by law.
          </p>
        </div>

        {/* Section 18 */}
        <div className="doc-section">
          <span className="section-number">Section 18</span>
          <h2 className="section-title">Changes to the Platform or Terms</h2>
          <p>
            MegaTransact may update these Terms and Conditions from time to time to reflect
            service developments, legal requirements, business changes, or operational
            improvements.
          </p>
          <p>
            When material changes are made, MegaTransact may notify users through the app,
            website, email, or other appropriate channels.
          </p>
          <p>
            Your continued use of MegaTransact after updated Terms take effect means you accept
            the revised Terms, unless applicable law requires a different process.
          </p>
        </div>

        {/* Section 19 */}
        <div className="doc-section">
          <span className="section-number">Section 19</span>
          <h2 className="section-title">Governing Law</h2>
          <p>
            These Terms and Conditions are governed by the laws applicable to MegaBliss LTD,
            unless another law is required by mandatory legal rules that apply to a particular
            service or user relationship.
          </p>
          <p>
            Any disputes arising from these Terms or from the use of MegaTransact will be handled
            in the legally appropriate forum, subject to applicable law.
          </p>
        </div>

        {/* Section 20 */}
        <div className="doc-section">
          <span className="section-number">Section 20</span>
          <h2 className="section-title">Contact Us</h2>
          <p>
            If you have questions, requests, or concerns about these Terms and Conditions, please
            contact:
          </p>
          <div className="doc-contact-box">
            <p>
              <strong>MegaTransact</strong>
            </p>
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
            <p>
              <strong>Company:</strong> MegaBliss LTD
            </p>
            <p>
              <strong>Address:</strong> Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;
